import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Loader, fmt, StatusBadge, fmtDateTime, EmptyState } from "@/components/shared";
import { AdminPageHeader } from "./adminShared";
import { ChevronLeft, ChevronRight } from "lucide-react";

const types = ["all", "deposit", "investment", "earnings", "principal_return", "referral_reward", "leaderboard_bonus", "withdrawal"];
const statuses = ["all", "pending", "completed", "failed"];

export default function AdminTransactions() {
  const [data, setData] = useState(null);
  const [type, setType] = useState("all");
  const [status, setStatus] = useState("all");
  const [page, setPage] = useState(1);

  const load = useCallback(async () => {
    setData(null);
    const r = await api.get("/admin/transactions", { params: { type, status, page, limit: 20 } });
    setData(r.data);
  }, [type, status, page]);
  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="admin-transactions">
      <AdminPageHeader eyebrow="Management" title="All Transactions"
        right={<div className="flex gap-2">
          <select data-testid="admin-tx-type" value={type} onChange={(e) => { setType(e.target.value); setPage(1); }} className="bg-slate-900/60 border border-cyan-500/20 rounded-lg px-3 py-2 text-sm text-slate-200 focus:outline-none">
            {types.map((t) => <option key={t} value={t} className="bg-slate-900 capitalize">{t === "all" ? "All Types" : t.replace("_", " ")}</option>)}
          </select>
          <select data-testid="admin-tx-status" value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }} className="bg-slate-900/60 border border-cyan-500/20 rounded-lg px-3 py-2 text-sm text-slate-200 focus:outline-none">
            {statuses.map((s) => <option key={s} value={s} className="bg-slate-900 capitalize">{s === "all" ? "All Status" : s}</option>)}
          </select>
        </div>} />
      {data === null ? <Loader /> : data.items.length ? (
        <>
          <div className="glass rounded-2xl p-4 overflow-x-auto">
            <table className="w-full text-sm min-w-[760px]">
              <thead><tr className="text-left text-slate-500 border-b border-white/5">
                {["ID", "User", "Type", "Amount", "Status", "Description", "Date"].map((h) => <th key={h} className="pb-3 px-2 font-medium">{h}</th>)}
              </tr></thead>
              <tbody>
                {data.items.map((t) => (
                  <tr key={t.id} className="border-b border-white/5 last:border-0">
                    <td className="py-3 px-2 font-mono text-xs text-slate-500">{t.id.slice(0, 8)}</td>
                    <td className="py-3 px-2 text-slate-300">{t.user_name}</td>
                    <td className="py-3 px-2 capitalize text-slate-200">{t.type.replace("_", " ")}</td>
                    <td className="py-3 px-2 font-mono text-white">{fmt(t.amount)}</td>
                    <td className="py-3 px-2"><StatusBadge status={t.status} /></td>
                    <td className="py-3 px-2 text-slate-400 max-w-[220px] truncate">{t.description}</td>
                    <td className="py-3 px-2 text-slate-500">{fmtDateTime(t.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between mt-4">
            <p className="text-xs text-slate-500">Page {data.page} of {data.pages} · {data.total} total</p>
            <div className="flex gap-2">
              <button disabled={page <= 1} onClick={() => setPage(page - 1)} className="glass px-3 py-1.5 rounded-lg text-sm text-slate-300 disabled:opacity-40 inline-flex items-center gap-1"><ChevronLeft className="w-4 h-4" /> Prev</button>
              <button disabled={page >= data.pages} onClick={() => setPage(page + 1)} className="glass px-3 py-1.5 rounded-lg text-sm text-slate-300 disabled:opacity-40 inline-flex items-center gap-1">Next <ChevronRight className="w-4 h-4" /></button>
            </div>
          </div>
        </>
      ) : <EmptyState title="No transactions found" />}
    </div>
  );
}
